import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import {
  selectAdminToken,
} from '../../admin/sessionSlice';
import { setBackButtonUrl } from '../uiSlice';
import { apiUrl } from '../../utils/apiUrl';
import { flattenRoundsToQuestions, themeUsesQuizRounds } from '../../utils/quizTheme';
import { addReportToServer } from '../../functions/sendReport';
import './game.css';

const S3 = (process.env.REACT_APP_S3_PATH || '').trim();
/** When uploads from `/uploadCategoryImage` are served under a different public URL than `REACT_APP_S3_PATH`, set this to that folder (e.g. `https://BUCKET.s3.REGION.amazonaws.com/UAT/light_camera_pop/`). */
const S3_GAME_UPLOAD_BASE = (process.env.REACT_APP_S3_GAME_UPLOAD_BASE || '').trim().replace(/\/+$/, '');

function joinPublicAssetUrl(base, pathOrFilename) {
  if (!pathOrFilename) return '';
  const s = String(pathOrFilename).trim();
  if (/^https?:\/\//i.test(s)) return s;
  const b = base.replace(/\/+$/, '');
  if (!b) return '';
  const rel = s.replace(/^\/+/, '');
  const joined = `${b}/${rel.split('/').map((seg) => encodeURIComponent(seg)).join('/')}`;
  return joined.replace(/%2F/g, '/');
}

function s3Url(filename) {
  return joinPublicAssetUrl(S3, filename);
}

/** Quiz question images: prefer `REACT_APP_S3_GAME_UPLOAD_BASE` so URLs match the upload bucket/prefix. */
function quizQuestionImageUrl(filename) {
  if (!filename) return '';
  const s = String(filename).trim();
  if (/^https?:\/\//i.test(s)) return s;
  if (S3_GAME_UPLOAD_BASE) return joinPublicAssetUrl(S3_GAME_UPLOAD_BASE, s);
  return s3Url(s);
}

const DEMO_SLIDE_LIMIT = 3;

/** Correct-answer celebration length (confetti + applause); keep in sync with `handleQuizOption` timeout. */
const QUIZ_CORRECT_CELEBRATION_MS = 3000;

/** Wrong-answer feedback duration (visual + wrong SFX); keep in sync with `handleQuizOption` delay. */
const QUIZ_FEEDBACK_MS = 2000;

/** Applause clip (S3); used for quiz correct-answer feedback. */
const APPLAUSE_SOUND_S3_URL =
  'https://2023-extramileplay-php.s3.ap-south-1.amazonaws.com/UAT/light_camera_pop/crowd-correct.mp3';

/** Wrong-answer clip (S3); played quieter than applause. */
const WRONG_SOUND_S3_URL =
  'https://2023-extramileplay-php.s3.ap-south-1.amazonaws.com/UAT/light_camera_pop/crowd-wrong.mp3';

/** Wrong SFX volume (0–1); correct applause stays at 1. */
const SOUND_VOLUME = 0.30;

/** In-game rules popup (quiz mode). */
const DEFAULT_QUIZ_RULES_LINES = [
  'The game has 6 fun rounds based on movies, TV shows, and pop culture',
  'Each question is MCQ so choose the correct answer',
  'Every correct answer gives you points',
  'Each round brings a different challenge like dialogues, emojis, places, costumes and more',
  'Complete all rounds and check your position on the leaderboard',
];

function publicImgUrl(filename) {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${base}/img/${filename}`;
}

function playApplauseFromS3(audioEl) {
  if (!audioEl) return;
  audioEl.pause();
  audioEl.currentTime = 0;
  audioEl.volume = SOUND_VOLUME;
  audioEl.src = APPLAUSE_SOUND_S3_URL;
  audioEl.load();
  const p = audioEl.play();
  if (p !== undefined) {
    p.catch(() => {});
  }
}

function playWrongFromS3(audioEl) {
  if (!audioEl) return;
  audioEl.pause();
  audioEl.currentTime = 0;
  audioEl.volume = SOUND_VOLUME;
  audioEl.src = WRONG_SOUND_S3_URL;
  audioEl.load();
  const p = audioEl.play();
  if (p !== undefined) {
    p.catch(() => {});
  }
}

function appBaseUrl() {
  const b = (process.env.REACT_APP_BASE_URL || '').trim().replace(/\/+$/, '');
  return b || window.location.origin;
}

function demoPlansUrl() {
  return `${appBaseUrl()}/plans`;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

// Deterministic PRNG from a string seed (ensures all users in the same
// session see the exact same slide order).
function makeRng(seed) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = ((h << 5) - h + seed.charCodeAt(i)) | 0;
  }
  return function next() {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    return h / 0x7fffffff;
  };
}

function seededShuffle(arr, rng) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function parseTimeToSeconds(t) {
  if (!t || typeof t !== 'string') return 0;
  const p = t.split(':').map((x) => parseInt(x, 10) || 0);
  if (p.length === 3) return p[0] * 3600 + p[1] * 60 + p[2];
  if (p.length === 2) return p[0] * 60 + p[1];
  return 0;
}

function formatMmSs(sec) {
  const t = Math.max(0, Math.floor(sec));
  const m = Math.floor(t / 60);
  const s = t % 60;
  const pad = (n) => (n < 10 ? `0${n}` : String(n));
  return `${pad(m)}:${pad(s)}`;
}

/** Integer points for `game.points` / APIs (avoids NaN → JSON null). */
function toApiPoints(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.floor(n));
}

/**
 * Build slide pairs from category_images_names.
 * Uses a seeded shuffle so every user in the same session (same seed)
 * gets the identical slide order — required for "% picked this" stats.
 */
function buildSlides(categoryImagesNames, max, seed) {
  const rng = makeRng(seed || 'default');
  const pairs = [];
  let cin = categoryImagesNames || {};
  if (typeof cin === 'string') {
    try { cin = JSON.parse(cin); } catch { cin = {}; }
  }
  if (typeof cin !== 'object' || Array.isArray(cin)) cin = {};

  // Sort category keys so iteration order is deterministic across browsers
  const sortedKeys = Object.keys(cin).sort();

  sortedKeys.forEach((category) => {
    const items = cin[category];
    if (!Array.isArray(items) || items.length < 2) return;
    for (let i = 0; i + 1 < items.length; i += 2) {
      pairs.push({
        left:  { ...items[i],     category },
        right: { ...items[i + 1], category },
      });
    }
  });

  return seededShuffle(pairs, rng).slice(0, max || 10);
}

// ── Item image with placeholder fallback ──────────────────────────────────
function ItemImg({ src }) {
  const [err, setErr] = useState(false);
  useEffect(() => { setErr(false); }, [src]);

  if (err || !src) {
    return (
      <div className="tot-item-placeholder">
        <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
          <rect width="32" height="32" rx="6" fill="rgba(255,255,255,0.25)" />
          <path d="M6 24l7-9 5 6 4-4 7 7H6z" fill="rgba(255,255,255,0.65)" />
          <circle cx="11" cy="11" r="3" fill="rgba(255,255,255,0.65)" />
        </svg>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt=""
      className="tot-item"
      draggable={false}
      referrerPolicy="no-referrer"
      onError={() => setErr(true)}
    />
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function Game() {
  const navigate    = useNavigate();
  const dispatch    = useDispatch();
  const { user }    = useSelector((s) => s.auth);
  const { data: themeData, status: themeStatus } = useSelector((s) => s.theme);
  const adminToken  = useSelector(selectAdminToken);

  const [statusChecked, setStatusChecked] = useState(false);
  const [alreadyDone, setAlreadyDone]     = useState(false);
  const [timerLabel, setTimerLabel]       = useState('00:00');
  const [resumeTimeLabel, setResumeTimeLabel] = useState('');
  const [hasResumeProgress, setHasResumeProgress] = useState(false);
  const [isMobile, setIsMobile]           = useState(false);

  // Slide state
  const [slides, setSlides]                 = useState([]);
  const [currentSlide, setCurrentSlide]     = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [slideAnim, setSlideAnim]           = useState('idle');
  const [animating, setAnimating]           = useState(false);
  const [gameOver, setGameOver]             = useState(false);
  const [selectedSide, setSelectedSide]     = useState(null); // 'left' | 'right' | null
  /** Quiz: after answering, { chosen, correct } until next slide (red wrong / green correct). */
  const [quizFeedback, setQuizFeedback]     = useState(null);
  /** Quiz: 'zoom' = question-only intro; 'ready' = answer grid visible & interactive. */
  const [quizIntroPhase, setQuizIntroPhase] = useState('zoom');
  /** Correct-answer confetti burst (bottom corners). */
  const [quizConfetti, setQuizConfetti] = useState(false);
  const [quizRulesOpen, setQuizRulesOpen] = useState(false);
  const [quizPoints, setQuizPoints]         = useState(0);
  /** Always in sync with latest quiz points so `spotSubmit` cannot read a stale closure. */
  const quizPointsRef = useRef(0);
  const [positionStats, setPositionStats]   = useState({});   // { "slideIdx": { itemName: count } }
  const [showPct, setShowPct]               = useState(false);
  const slidesRef = useRef([]);
  const quizPickLockRef = useRef(false);
  const applauseAudioRef = useRef(null);
  const applauseStopTimerRef = useRef(null);
  const wrongAudioRef = useRef(null);
  const wrongStopTimerRef = useRef(null);

  const totalTimeRef         = useRef(0);
  const initialCountdownRef  = useRef(0);
  const minutesSecondRef     = useRef('00:00');
  const useTimeoutRef        = useRef(false);
  const submittingRef        = useRef(false);
  const saveDataRef          = useRef(() => {});
  const answersRef           = useRef([]);
  const reportIdRef          = useRef(user?.reportId ?? null);

  const themeKey  = themeData?.themename || '';
  const useTimeout = themeData?.use_timeout === true || themeData?.use_timeout === 'true';
  const defaultTime = useMemo(() => (!themeData || !useTimeout) ? '00:00' : (themeData.timer || '00:00'), [themeData, useTimeout]);
  const numberOfSlides = Number(themeData?.number_of_slides) || 10;
  const isDemoUser = user?.source === 'DEMO';
  const effectiveSlideCount = isDemoUser ? DEMO_SLIDE_LIMIT : numberOfSlides;
  const quizMode = themeUsesQuizRounds(themeData);
  const quizRulesLines = useMemo(() => {
    const rules = Array.isArray(themeData?.rules) ? themeData.rules : [];
    const cleaned = rules
      .map((line) => (typeof line === 'string' ? line.trim() : ''))
      .filter(Boolean);
    return cleaned.length > 0 ? cleaned : DEFAULT_QUIZ_RULES_LINES;
  }, [themeData?.rules]);

  const btnBgColor   = themeData?.button_backgroundcolor || '#8b4513';
  const btnTextColor = themeData?.button_color || '#ffffff';

  /** Quiz rules modal: title + list colors from theme (fallbacks match default CSS). */
  const quizRulesTitleColor =
    themeData?.rules_title_color || themeData?.quiz_rules_title_color || '';
  const quizRulesBodyColor =
    themeData?.rules_text_color || themeData?.text_color || '';

  const resolvedUserName = useMemo(() => {
    if (!user) return '';
    const raw = (user.name || '').trim();
    if (raw && raw !== 'null' && raw !== 'null null') return raw;
    if (user.source === 'GUEST_USER') {
      return user.email ? user.email.split('@')[0] : (user.employeeId || user.userId || '');
    }
    return user.email ? user.email.split('@')[0] : (user.employeeId || user.userId || '');
  }, [user]);

  // Keep answersRef in sync
  useEffect(() => { answersRef.current = selectedAnswers; }, [selectedAnswers]);
  useEffect(() => { quizPointsRef.current = quizPoints; }, [quizPoints]);

  useEffect(() => {
    if (user?.reportId) {
      reportIdRef.current = user.reportId;
      return;
    }
    if (user?.userId && user?.sessionId) {
      try {
        const s = sessionStorage.getItem(`tot_report_${user.sessionId}_${user.userId}`);
        if (s) reportIdRef.current = s;
      } catch {
        /* ignore */
      }
    }
  }, [user?.reportId, user?.userId, user?.sessionId]);

  // Layout: narrow portrait/tablet, or short landscape (phones rotated, small laptop heights)
  useEffect(() => {
    const mqNarrow = window.matchMedia('(max-width: 768px)');
    const mqShortLandscape = window.matchMedia('(max-height: 480px) and (orientation: landscape)');
    const update = () => setIsMobile(mqNarrow.matches || mqShortLandscape.matches);
    update();
    const add = (m) => (m.addEventListener ? m.addEventListener('change', update) : m.addListener(update));
    const rem = (m) => (m.removeEventListener ? m.removeEventListener('change', update) : m.removeListener(update));
    add(mqNarrow);
    add(mqShortLandscape);
    return () => {
      rem(mqNarrow);
      rem(mqShortLandscape);
    };
  }, []);

  useEffect(() => { dispatch(setBackButtonUrl('/login?&save=true'));   }, [dispatch]);

  useEffect(() => {
    if (!quizRulesOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setQuizRulesOpen(false);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [quizRulesOpen]);

  useEffect(() => {
    if (typeof Audio === 'undefined') return undefined;
    const a = new Audio(APPLAUSE_SOUND_S3_URL);
    a.preload = 'auto';
    a.volume = 1;
    applauseAudioRef.current = a;
    return () => {
      if (applauseStopTimerRef.current) {
        clearTimeout(applauseStopTimerRef.current);
        applauseStopTimerRef.current = null;
      }
      a.pause();
      a.src = '';
      applauseAudioRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (typeof Audio === 'undefined') return undefined;
    const w = new Audio(WRONG_SOUND_S3_URL);
    w.preload = 'auto';
    w.volume = SOUND_VOLUME;
    wrongAudioRef.current = w;
    return () => {
      if (wrongStopTimerRef.current) {
        clearTimeout(wrongStopTimerRef.current);
        wrongStopTimerRef.current = null;
      }
      w.pause();
      w.src = '';
      wrongAudioRef.current = null;
    };
  }, []);

  // Helper: fetch resume progress and position stats from MySQL.
  const fetchStats = useCallback(async () => {
    if (!adminToken || !user) return null;
    try {
      const resp = await fetch(apiUrl('/itemPickStats'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ userId: user.userId }),
      });
      const json = await resp.json();
      if (json.success) return json;
    } catch (e) {
      console.error('itemPickStats', e);
    }
    return null;
  }, [adminToken, user]);

  // Build slides + fetch stats + resume — single effect
  useEffect(() => {
    const hasQuiz = themeUsesQuizRounds(themeData);
    const hasLegacy = !!themeData?.category_images_names;
    if ((!hasQuiz && !hasLegacy) || !user || !adminToken || themeStatus !== 'succeeded') return;
    let cancelled = false;

    const seed = themeData.themename || themeData.themeName || 'default';
    const built = hasQuiz
      ? flattenRoundsToQuestions(themeData.rounds, effectiveSlideCount)
      : buildSlides(themeData.category_images_names, effectiveSlideCount, seed);
    setSlides(built);
    slidesRef.current = built;
    setGameOver(false);
    setSlideAnim('idle');
    setAnimating(false);
    setQuizFeedback(null);
    setQuizPoints(0);
    quizPointsRef.current = 0;
    quizPickLockRef.current = false;

    (async () => {
      const json = await fetchStats();
      if (cancelled) return;
      if (json) {
        setPositionStats(json.positionStats || {});
        const persistedTime = typeof json.userTime === 'string' ? json.userTime.trim() : '';
        const persistedProgress = json.userHasProgress === true;
        setHasResumeProgress(persistedProgress);
        setResumeTimeLabel(persistedTime);
        if (quizMode) {
          const persistedPoints = Number(json.userPoints);
          const safePoints = persistedProgress && Number.isFinite(persistedPoints) && persistedPoints >= 0 ? persistedPoints : 0;
          setQuizPoints(safePoints);
          quizPointsRef.current = safePoints;
        }
        const userAnswers = json.userAnswers || [];
        if (userAnswers.length >= built.length) {
          setAlreadyDone(true);
          if (user?.source === 'DEMO') {
            window.location.replace(demoPlansUrl());
            return;
          }
          navigate('/feedback', { replace: true });
          return;
        }
        setSelectedAnswers(userAnswers);
        answersRef.current = userAnswers;
        setCurrentSlide(userAnswers.length);
      } else {
        setHasResumeProgress(false);
        setResumeTimeLabel('');
        setCurrentSlide(0);
        setSelectedAnswers([]);
        answersRef.current = [];
      }
      setStatusChecked(true);
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    themeData?.category_images_names,
    themeData?.rounds,
    effectiveSlideCount,
    themeData?.themename,
    user,
    adminToken,
    themeStatus,
    navigate,
  ]);

  // MySQL-backed polling for live %.
  useEffect(() => {
    if (!statusChecked || gameOver || alreadyDone) return;
    const interval = setInterval(async () => {
      const json = await fetchStats();
      if (json) setPositionStats(json.positionStats || {});
    }, 3000);
    return () => clearInterval(interval);
  }, [statusChecked, gameOver, alreadyDone, fetchStats]);

  // ── Save to DB ─────────────────────────────────────────────────────────────
  const saveData = useCallback(async (answers) => {
    if (submittingRef.current) return;
    if (!user || !adminToken || !themeData) return;
    submittingRef.current = true;

    let timeToSave = minutesSecondRef.current;
    if (useTimeoutRef.current && initialCountdownRef.current > 0) {
      const elapsed = Math.min(initialCountdownRef.current,
        Math.max(0, initialCountdownRef.current - totalTimeRef.current));
      timeToSave = formatMmSs(elapsed);
    }

    const ans       = answers || [];
    const spotScore = ans.length;
    const pointsToSave = quizMode ? toApiPoints(quizPointsRef.current) : toApiPoints(spotScore);

    try {
      const resp = await fetch(apiUrl('/spotSubmit'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          userId:       user.userId,
          email:        user.email || '',
          name:         resolvedUserName,
          time:         timeToSave,
          spot_ans:     JSON.stringify(ans),
          spot_score:   spotScore,
          total_score:  spotScore,
          points:       pointsToSave,
          currentTheme: themeData.themename || themeData.themeName,
        }),
      });

      const text    = await resp.text();
      let payload   = null;
      try { payload = text ? JSON.parse(text) : null; } catch { payload = null; }

      if (!resp.ok) {
        const msg = payload?.error || text || 'Submit failed';
        await Swal.fire({ icon: 'error', title: 'Submit failed', text: msg });
        submittingRef.current = false;
        return;
      }

      if (payload?.demo === true || text?.trim() === 'demo' || user?.source === 'DEMO') {
        const msg = payload?.uniqueMsg || 'Thanks for playing (demo).';
        sessionStorage.setItem('spotThankYouMsg', msg);
        await Swal.fire({ icon: 'success', title: 'Thank you for playing.', text: 'Subscribe to any PLAN to play with your peers.' });
        window.location.href = demoPlansUrl();
        return;
      }

      navigate('/feedback', { replace: true });
    } catch (e) {
      console.error('spotSubmit', e);
      await Swal.fire({ icon: 'error', title: 'Network error', text: String(e.message) });
    }
    submittingRef.current = false;
  }, [user, adminToken, themeData, navigate, quizMode, resolvedUserName]);

  useEffect(() => { saveDataRef.current = saveData; }, [saveData]);

  // ── Timer ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!statusChecked || !themeData || alreadyDone) return;

    useTimeoutRef.current = useTimeout;
    const sec = parseTimeToSeconds(defaultTime);
    const resumedSec = parseTimeToSeconds(resumeTimeLabel);
    const hasResumeTime = hasResumeProgress && (resumedSec > 0 || resumeTimeLabel === '00:00');
    if (useTimeout) {
      initialCountdownRef.current = sec;
      totalTimeRef.current = hasResumeTime
        ? Math.max(0, Math.min(sec, resumedSec))
        : sec;
    } else {
      initialCountdownRef.current = 0;
      totalTimeRef.current = hasResumeTime
        ? Math.max(0, resumedSec)
        : 0;
    }

    const initialLabel = formatMmSs(
      useTimeout
        ? Math.max(0, totalTimeRef.current)
        : totalTimeRef.current
    );
    minutesSecondRef.current = initialLabel;
    setTimerLabel(initialLabel);

    const tick = () => {
      if (useTimeoutRef.current) totalTimeRef.current -= 1;
      else totalTimeRef.current += 1;
      const t = totalTimeRef.current;
      const display = formatMmSs(useTimeoutRef.current ? Math.max(0, t) : t);
      minutesSecondRef.current = display;
      setTimerLabel(display);
      if (useTimeoutRef.current && t <= 0) {
        clearInterval(id);
        saveDataRef.current(answersRef.current);
      }
    };

    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusChecked, themeKey, alreadyDone, useTimeout, defaultTime, resumeTimeLabel, hasResumeProgress]);

  // ── Choice handler (3-second highlight then slide) ─────────────────────────
  const HIGHLIGHT_MS = 2000;
  const SLIDE_MS     = 420;
  /** Time before options show (keep in sync with quiz zoom duration in game.css, ~1.35s). */
  const QUIZ_INTRO_MS = 1200;

  useLayoutEffect(() => {
    if (!quizMode || gameOver || !slides.length) return;
    setQuizIntroPhase('zoom');
  }, [quizMode, gameOver, currentSlide, slides.length]);

  useEffect(() => {
    if (!quizMode || gameOver || !slides.length) return;
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const delay = reduce ? 0 : QUIZ_INTRO_MS;
    const id = window.setTimeout(() => setQuizIntroPhase('ready'), delay);
    return () => window.clearTimeout(id);
  }, [quizMode, gameOver, currentSlide, slides.length]);

  // Compute percentage for a slide position using position-based stats.
  // JSON keys are always strings, so we convert slideIdx to string.
  const getPairPct = useCallback((slideIdx, leftName, rightName) => {
    const key = String(slideIdx);
    const slideStats = positionStats[key] || {};
    const lc = slideStats[leftName] || 0;
    const rc = slideStats[rightName] || 0;
    const total = lc + rc;
    if (total === 0) return { left: 0, right: 0 };
    return {
      left:  Math.round((lc / total) * 100),
      right: Math.round((rc / total) * 100),
    };
  }, [positionStats]);

  const handleChoice = useCallback((chosenItem, side) => {
    if (animating || gameOver) return;

    const slideIdx = currentSlide;
    const newAnswers = [...answersRef.current, chosenItem.name];
    setSelectedAnswers(newAnswers);
    answersRef.current = newAnswers;

    // Optimistically update position stats so the badge shows immediately.
    // Use string key to match JSON format from the API.
    const key = String(slideIdx);
    setPositionStats((prev) => {
      const slideStats = { ...(prev[key] || {}) };
      slideStats[chosenItem.name] = (slideStats[chosenItem.name] || 0) + 1;
      return { ...prev, [key]: slideStats };
    });

    // Save this pick to MySQL (resume / leaderboard).
    if (user && adminToken) {
      fetch(apiUrl('/saveSlideAnswer'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          userId: user.userId,
          email:  user.email || '',
          name:   resolvedUserName,
          itemName: chosenItem.name,
          slideIndex: slideIdx,
          points: toApiPoints(newAnswers.length),
          time: minutesSecondRef.current,
        }),
      })
        .then((r) => r.json())
        .then((json) => {
          if (json.success && json.positionStats) {
            setPositionStats(json.positionStats);
          }
        })
        .catch((e) => console.error('saveSlideAnswer', e));
    }

    addReportToServer(user, {
      points: toApiPoints(newAnswers.length),
      reportTime: minutesSecondRef.current,
      answers: newAnswers,
      reportIdRef,
    });

    setSelectedSide(side);
    setShowPct(true);
    setAnimating(true);

    setTimeout(() => setShowPct(false), 1200);

    const nextIndex = currentSlide + 1;

    setTimeout(() => {
      if (nextIndex >= slides.length) {
        setGameOver(true);
        setSelectedSide(null);
        setTimeout(() => saveDataRef.current(newAnswers), 300);
        return;
      }

      setSlideAnim('exit');
      setTimeout(() => {
        setSelectedSide(null);
        setCurrentSlide(nextIndex);
        setSlideAnim('enter');
        setTimeout(() => {
          setSlideAnim('idle');
          setAnimating(false);
        }, SLIDE_MS);
      }, SLIDE_MS);
    }, HIGHLIGHT_MS);
  }, [
    animating,
    gameOver,
    currentSlide,
    slides.length,
    user,
    adminToken,
    resolvedUserName,
  ]);

  const handleQuizOption = useCallback((optionText, optionIdx) => {
    if (animating || gameOver || quizPickLockRef.current) return;

    const slideIdx = currentSlide;
    const currentQ = slidesRef.current[slideIdx];
    let nextQuizPoints = quizPointsRef.current;
    if (currentQ && Number(currentQ.correctIndex) === optionIdx) {
      const per = Number(themeData?.points);
      const add = Number.isFinite(per) && per > 0 ? per : 1;
      nextQuizPoints = quizPointsRef.current + add;
      setQuizPoints(nextQuizPoints);
    }
    quizPointsRef.current = nextQuizPoints;

    const newAnswers = [...answersRef.current, optionText];
    setSelectedAnswers(newAnswers);
    answersRef.current = newAnswers;

    const key = String(slideIdx);
    setPositionStats((prev) => {
      const slideStats = { ...(prev[key] || {}) };
      slideStats[optionText] = (slideStats[optionText] || 0) + 1;
      return { ...prev, [key]: slideStats };
    });

    if (user && adminToken) {
      fetch(apiUrl('/saveSlideAnswer'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          userId: user.userId,
          email:  user.email || '',
          name:   resolvedUserName,
          itemName: optionText,
          slideIndex: slideIdx,
          points: toApiPoints(nextQuizPoints),
          time: minutesSecondRef.current,
        }),
      })
        .then((r) => r.json())
        .then((json) => {
          if (json.success && json.positionStats) {
            setPositionStats(json.positionStats);
          }
        })
        .catch((e) => console.error('saveSlideAnswer', e));
    }

    addReportToServer(user, {
      points: toApiPoints(nextQuizPoints),
      reportTime: minutesSecondRef.current,
      answers: newAnswers,
      reportIdRef,
    });

    const correctIdx = Number(currentQ?.correctIndex);
    const safeCorrect = Number.isInteger(correctIdx) && correctIdx >= 0 ? correctIdx : 0;
    const isCorrectPick = optionIdx === safeCorrect;
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (isCorrectPick && !reduceMotion) setQuizConfetti(true);

    if (isCorrectPick) {
      const w = wrongAudioRef.current;
      if (w && wrongStopTimerRef.current) {
        clearTimeout(wrongStopTimerRef.current);
        wrongStopTimerRef.current = null;
        w.pause();
        w.currentTime = 0;
      }
      const a = applauseAudioRef.current;
      if (a) {
        if (applauseStopTimerRef.current) {
          clearTimeout(applauseStopTimerRef.current);
          applauseStopTimerRef.current = null;
        }
        playApplauseFromS3(a);
        applauseStopTimerRef.current = window.setTimeout(() => {
          a.pause();
          a.currentTime = 0;
          applauseStopTimerRef.current = null;
        }, QUIZ_CORRECT_CELEBRATION_MS);
      }
    } else if (!reduceMotion) {
      const a = applauseAudioRef.current;
      if (a && applauseStopTimerRef.current) {
        clearTimeout(applauseStopTimerRef.current);
        applauseStopTimerRef.current = null;
        a.pause();
        a.currentTime = 0;
      }
      const w = wrongAudioRef.current;
      if (w) {
        if (wrongStopTimerRef.current) {
          clearTimeout(wrongStopTimerRef.current);
          wrongStopTimerRef.current = null;
        }
        playWrongFromS3(w);
        wrongStopTimerRef.current = window.setTimeout(() => {
          w.pause();
          w.currentTime = 0;
          wrongStopTimerRef.current = null;
        }, QUIZ_FEEDBACK_MS);
      }
    }

    quizPickLockRef.current = true;
    setQuizFeedback({ chosen: optionIdx, correct: safeCorrect });
    setAnimating(true);

    const nextIndex = currentSlide + 1;
    const feedbackDelayMs = isCorrectPick ? QUIZ_CORRECT_CELEBRATION_MS : QUIZ_FEEDBACK_MS;

    setTimeout(() => {
      setQuizConfetti(false);
      setQuizFeedback(null);
      if (nextIndex >= slides.length) {
        setGameOver(true);
        quizPickLockRef.current = false;
        setAnimating(false);
        setTimeout(() => saveDataRef.current(newAnswers), 300);
        return;
      }

      setSlideAnim('exit');
      setTimeout(() => {
        setCurrentSlide(nextIndex);
        setSlideAnim('enter');
        setTimeout(() => {
          setSlideAnim('idle');
          setAnimating(false);
          quizPickLockRef.current = false;
        }, SLIDE_MS);
      }, SLIDE_MS);
    }, feedbackDelayMs);
  }, [
    animating,
    gameOver,
    currentSlide,
    slides.length,
    user,
    adminToken,
    resolvedUserName,
    themeData?.points,
  ]);

  // ── Derived layout values ──────────────────────────────────────────────────
  const sceneBg  = isMobile
    ? (themeData?.background_mob  ? `url(${s3Url(themeData.background_mob)})`  : 'none')
    : (themeData?.background_desk ? `url(${s3Url(themeData.background_desk)})` : 'none');

  const boxBg = isMobile
    ? (themeData?.mobile_box  ? `url(${s3Url(themeData.mobile_box)})`  : 'none')
    : (themeData?.desktop_box ? `url(${s3Url(themeData.desktop_box)})` : 'none');

  const gameTitle = themeData?.landing_page_title || 'World Charms';
  const slide     = slides[currentSlide];
  const pairPct =
    !quizMode && slide?.left?.name && slide?.right?.name
      ? getPairPct(currentSlide, slide.left.name, slide.right.name)
      : null;
  // ── Guards ─────────────────────────────────────────────────────────────────
  if (!user) { navigate('/login', { replace: true }); return null; }

  if (themeStatus !== 'succeeded' || !themeData || !statusChecked) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  /* Text: quote in .tot-quiz-headline. Image: no headline; round lines in .tot-quiz-round-block. */
  const quizHeadline =
    quizMode && slide && slide.promptType !== 'image'
      ? (slide.questionText || '')
      : '';

  return (
    <div
      className={`tot-scene${isMobile ? ' is-mobile' : ''}${quizMode ? ' tot-scene--quiz' : ''}${quizMode && quizHeadline ? ' tot-scene--quiz-text-prompt' : ''}${quizMode && slide?.promptType === 'image' ? ' tot-scene--quiz-image' : ''}`}
      style={{
        backgroundImage: sceneBg,
        ...(quizMode && {
          '--quiz-opt-fg': themeData?.quiz_option_text_color || '#0d1b5c',
          '--quiz-opt-bg': themeData?.quiz_option_background_color || '#eceff1',
          '--quiz-q-fg': themeData?.quiz_question_text_color || '#1a1a1a',
          '--quiz-q-bg': themeData?.quiz_question_background_color || '#ffffff',
        }),
      }}
      aria-label="World Charms"
    >
      {quizMode && quizConfetti && (
        <div className="tot-quiz-confetti" aria-hidden="true">
          {Array.from({ length: 72 }, (_, i) => {
            const hue = (i * 41) % 360;
            return (
              <span
                key={`cf-bl-${i}`}
                className={`tot-quiz-confetti-dot tot-quiz-confetti-dot--bl${i % 2 === 0 ? ' tot-quiz-confetti-dot--round' : ''}`}
                style={{
                  '--tx': `${14 + ((i * 37) % 68)}vw`,
                  '--ty': `${-(32 + ((i * 17) % 62))}vh`,
                  '--delay': `${(i % 22) * 0.028}s`,
                  backgroundColor: `hsl(${hue}, 100%, 58%)`,
                  boxShadow: `0 0 16px 4px hsla(${hue}, 100%, 52%, 0.65), 0 0 28px 6px hsla(${hue}, 100%, 55%, 0.35)`,
                }}
              />
            );
          })}
          {Array.from({ length: 72 }, (_, i) => {
            const hue = (i * 47 + 55) % 360;
            return (
              <span
                key={`cf-br-${i}`}
                className={`tot-quiz-confetti-dot tot-quiz-confetti-dot--br${i % 2 === 0 ? ' tot-quiz-confetti-dot--round' : ''}`}
                style={{
                  '--tx': `${-(14 + ((i * 39) % 68))}vw`,
                  '--ty': `${-(34 + ((i * 19) % 62))}vh`,
                  '--delay': `${(i % 22) * 0.028}s`,
                  backgroundColor: `hsl(${hue}, 100%, 56%)`,
                  boxShadow: `0 0 16px 4px hsla(${hue}, 100%, 50%, 0.65), 0 0 28px 6px hsla(${hue}, 100%, 54%, 0.35)`,
                }}
              />
            );
          })}
        </div>
      )}

      {/* Quiz HUD: time (left) + points & rules (right) */}
      {quizMode && slides.length > 0 && !gameOver && (
        <>
          <div className="tot-quiz-hud-cluster tot-quiz-hud-cluster--left">
            <div
              className="tot-quiz-hud-surface tot-quiz-hud-time"
              style={{ color: btnTextColor }}
            >
              <img
                src={publicImgUrl('timer-img.png')}
                alt=""
                aria-hidden="true"
                className="tot-quiz-hud-icon tot-quiz-hud-icon--timer"
                draggable={false}
              />
              <span className="tot-quiz-hud-surface__value tot-quiz-hud-surface__value--mono">{timerLabel}</span>
            </div>
            <button
              type="button"
              className="tot-quiz-hud-surface tot-quiz-hud-rules-btn"
              style={{ color: btnTextColor, border: 'none', outline: 'none', backgroundColor: 'transparent' }}
              onClick={() => setQuizRulesOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={quizRulesOpen}
              aria-label="Rules"
            >
              <img
                src={publicImgUrl('rules-img.png')}
                alt=""
                aria-hidden="true"
                className="tot-quiz-hud-icon tot-quiz-hud-icon--rules"
                draggable={false}
              />
            </button>
          </div>
          <div className="tot-quiz-hud-cluster tot-quiz-hud-cluster--right">
            <div
              className="tot-quiz-hud-surface tot-quiz-hud-points"
              style={{  color: btnTextColor }}
            >
              <img
                src={publicImgUrl('point-img.png')}
                alt=""
                aria-hidden="true"
                className="tot-quiz-hud-icon tot-quiz-hud-icon--points"
                draggable={false}
              />
              <span className="tot-quiz-hud-surface__value">{quizPoints}</span>
            </div>
          </div>

          {quizRulesOpen ? (
            <div
              className="tot-quiz-rules-overlay"
              role="presentation"
              onClick={() => setQuizRulesOpen(false)}
            >
              <div
                className="tot-quiz-rules-dialog" 
                role="dialog"
                aria-modal="true"
                aria-labelledby="tot-quiz-rules-title"
                onClick={(e) => e.stopPropagation()}
                style={{
                  ...(quizRulesTitleColor && { '--tot-quiz-rules-title-color': quizRulesTitleColor }),
                  ...(quizRulesBodyColor && { '--tot-quiz-rules-body-color': quizRulesBodyColor, 'overflow': 'visible' }),
                }}
              >
                <div className="tot-quiz-rules-badge" aria-hidden="true">
                  <img
                    src={publicImgUrl('rules-img.png')}
                    alt=""
                    className="tot-quiz-rules-badge__img"
                    draggable={false}
                  />
                </div>
                <button
                  type="button"
                  className="tot-quiz-rules-close"
                  onClick={() => setQuizRulesOpen(false)}
                  aria-label="Close rules"
                >
                  <i className="fa-solid fa-xmark" aria-hidden />
                </button>
                <h2 id="tot-quiz-rules-title" className="tot-quiz-rules-title" style={{ color: themeData?.rules_text_color  }}>
                  How to Play
                </h2>
                <ul className="tot-quiz-rules-list">
                  {quizRulesLines.map((line) => (
                    <li style={{ color: themeData?.rules_text_color  }} key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}
        </>
      )}

      {/* Legacy pair-game timer (countdown only) */}
      {!quizMode && useTimeout && (
        <div
          className="tot-timer"
          style={{ backgroundColor: btnBgColor, color: btnTextColor }}
        >
          {timerLabel}
        </div>
      )}

      {/* Landing title — hidden in quiz mode; question lives in .tot-quiz-headline */}
      {!quizMode && (
        <h1
          className="tot-title"
          style={{ ...(themeData?.text_color && { color: themeData.text_color }) }}
          dangerouslySetInnerHTML={{ __html: gameTitle }}
        />
      )}

      {quizMode && slide && (slide.roundLabel || slide.roundSubtitle) && (
        <div className="tot-quiz-round-block" aria-live="polite">
          {String(slide.roundLabel || '').trim() ? (
            <div className="tot-quiz-round-label" style={{ color: themeData?.text_color }}>{String(slide.roundLabel).trim()}</div>
          ) : null}
          {String(slide.roundSubtitle || '').trim() ? (
            <div className="tot-quiz-round-subtitle" style={{ color: themeData?.text_color }}>{String(slide.roundSubtitle).trim()}</div>
          ) : null}
        </div>
      )}

      {quizMode && slide && quizHeadline && (
        <div
          className={`tot-quiz-headline${quizIntroPhase === 'zoom' ? ' tot-quiz-prompt-zoom' : ' tot-quiz-prompt-settled'}`}
          aria-live="polite"
        >
          {quizHeadline}
        </div>
      )}

      {/* Slide counter — pair game only (not quiz) */}
      {!quizMode && slides.length > 0 && (
        <div className="tot-slide-counter" style={{ ...(themeData?.text_color && { color: themeData.text_color }) }}>
          <span className="tot-slide-current">{currentSlide + 1}</span>
          <span className="tot-slide-sep">/</span>
          <span className="tot-slide-total">{slides.length}</span>
        </div>
      )}

      {/* Game over overlay */}
      {gameOver ? (
        <div className="tot-gameover">
          <div
            className="tot-gameover-msg"
            style={{ backgroundColor: btnBgColor, color: btnTextColor }}
          >
            Saving your answers…
          </div>
        </div>
      ) : slide ? (
        quizMode ? (
        <section
          key={`slide-${currentSlide}`}
          className={`tot-choices tot-quiz tot-quiz--${slide.promptType === 'image' ? 'image' : 'text'}`}
          aria-label={`Question ${currentSlide + 1}`}
        >
          {slide.promptType === 'image' && (
            <div
              className={`tot-qimg-wrap${quizIntroPhase === 'zoom' ? ' tot-quiz-prompt-zoom' : ' tot-quiz-prompt-settled'}`}
            >
              <ItemImg src={slide.questionImage ? quizQuestionImageUrl(slide.questionImage) : ''} />
            </div>
          )}
          <div
            className={`tot-opt-grid${quizFeedback ? ' tot-opt-grid--locked' : ''}${
              quizIntroPhase === 'zoom' ? ' tot-opt-grid--intro-wait' : ' tot-opt-grid--slide-in'
            }`}
            role="group"
            aria-label="Answer choices"
          >
            {slide.options.map((opt, i) => {
              const fb = quizFeedback;
              const isCorrect = fb && i === fb.correct;
              const isWrongPick = fb && i === fb.chosen && fb.chosen !== fb.correct;
              const isMuted = fb && i !== fb.correct && i !== fb.chosen;
              const useThemeFill = !fb || (!isCorrect && !isWrongPick);
              /* MCQ: always flat + CSS vars — desktop_box as background-image would hide admin quiz colors. */
              return (
                <button
                  key={`${i}-${opt}`}
                  type="button"
                  className={`tot-opt-btn tot-opt-btn-flat${isCorrect ? ' tot-opt-correct' : ''}${isWrongPick ? ' tot-opt-wrong' : ''}${isMuted ? ' tot-opt-muted' : ''}`}
                  style={
                    useThemeFill
                      ? { backgroundColor: 'var(--quiz-opt-bg)', color: 'var(--quiz-opt-fg)' }
                      : undefined
                  }
                  disabled={animating || !!fb || quizIntroPhase !== 'ready'}
                  onClick={() => handleQuizOption(opt, i)}
                  aria-label={opt}
                >
                  <span className="tot-opt-label">{opt}</span>
                </button>
              );
            })}
          </div>
        </section>
        ) : (
        <section
          key={`slide-${currentSlide}`}
          className={`tot-choices tot-anim-${slideAnim}`}
          aria-label={`Slide ${currentSlide + 1}`}
        >
          {/* Left card */}
          <article
            className={`tot-choice-card${boxBg !== 'none' ? '' : ' tot-card-placeholder'}${selectedSide === 'left' ? ' tot-selected' : ''}${selectedSide === 'right' ? ' tot-not-selected' : ''}`}
            style={boxBg !== 'none' ? { backgroundImage: boxBg } : undefined}
            onClick={() => handleChoice(slide.left, 'left')}
            role="button"
            tabIndex={0}
            aria-label="Choose left"
            onKeyDown={(e) => e.key === 'Enter' && handleChoice(slide.left, 'left')}
          >
            <ItemImg src={slide.left.image ? s3Url(slide.left.image) : ''} />
            {selectedSide && pairPct && (
              <div className={`tot-pct-badge${selectedSide === 'left' ? ' tot-pct-selected' : ''}${!showPct ? ' tot-pct-hide' : ''}`}>
                <span className="tot-pct-value">{pairPct.left}%</span>
                <span className="tot-pct-label">picked this</span>
              </div>
            )}
          </article>

          {/* Right card */}
          <article
            className={`tot-choice-card${boxBg !== 'none' ? '' : ' tot-card-placeholder'}${selectedSide === 'right' ? ' tot-selected' : ''}${selectedSide === 'left' ? ' tot-not-selected' : ''}`}
            style={boxBg !== 'none' ? { backgroundImage: boxBg } : undefined}
            onClick={() => handleChoice(slide.right, 'right')}
            role="button"
            tabIndex={0}
            aria-label="Choose right"
            onKeyDown={(e) => e.key === 'Enter' && handleChoice(slide.right, 'right')}
          >
            <ItemImg src={slide.right.image ? s3Url(slide.right.image) : ''} />
            {selectedSide && pairPct && (
              <div className={`tot-pct-badge${selectedSide === 'right' ? ' tot-pct-selected' : ''}${!showPct ? ' tot-pct-hide' : ''}`}>
                <span className="tot-pct-value">{pairPct.right}%</span>
                <span className="tot-pct-label">picked this</span>
              </div>
            )}
          </article>
        </section>
        )
      ) : (
        <div className="tot-loading">Loading items…</div>
      )}
    </div>
  );
}
